import { DocumentNode, gql } from '@apollo/client';
import { Button, ButtonProps, useDisclosure } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { useCurrent } from '@/hooks/useCurrent';
import { usePermissions } from '@/hooks/usePermissions';
import { useAnalyticsLogger } from '@/imports/ui/amplitude/helpers';

import { TicketedEventRegistrationButton_EventFragment } from './graphql-types/TicketedEventRegistrationButton_EventFragment';

import { EventRegistrationModal } from './components/EventRegistrationModal';
import { ExternalRegistrationModal } from './components/ExternalRegistrationModal';

export const TicketedEventRegistrationButton: React.FC<
  {
    event: TicketedEventRegistrationButton_EventFragment;
  } & ButtonProps
> & {
  eventFragment: DocumentNode;
} = ({ event, children, ...props }) => {
  const router = useRouter();

  const rModalOpen = router.query.rModalOpen
    ? router.query.rModalOpen.toString()
    : undefined;

  const ticketLinkId = router.query.ticketLinkId
    ? router.query.ticketLinkId.toString()
    : undefined;

  const preSelectedTicketId = router.query.preSelectedTicketId
    ? router.query.preSelectedTicketId.toString()
    : undefined;

  const permissions = usePermissions();

  const registrationModalDisclosure = useDisclosure({
    defaultIsOpen: !!rModalOpen || !!ticketLinkId || !!preSelectedTicketId,
  });

  const current = useCurrent();
  const analyticsClient = useAnalyticsLogger();

  React.useEffect(() => {
    if (router.isReady && !!rModalOpen) {
      analyticsClient.logEvent_events_click_register({
        'logged in': !!current.user?._id,
        'event id': event._id,
        'ticket options': event.ticketsCurrentUserCanPurchase?.length || 0,
        'event start date': new Date(event.startDate).toISOString(),
      });
      const { rModalOpen, ...query } = router.query;
      router.replace({ query });
    }
  }, [analyticsClient, event, router, current.user?._id, rModalOpen]);

  return permissions.isLoading ? null : (
    <>
      <Button
        onClick={() => {
          analyticsClient.logEvent_events_click_register({
            'logged in': permissions.isAuthenticated,
            'event id': event._id,
            'ticket options': event.ticketsCurrentUserCanPurchase?.length || 0,
            'event start date': event.startDate,
          });

          registrationModalDisclosure.onOpen();
        }}
        {...props}
      >
        {children ? children : 'Register'}
      </Button>
      {event.isExternalRegistrationUrlActive &&
      event.externalRegistrationUrl ? (
        <ExternalRegistrationModal
          isOpen={registrationModalDisclosure.isOpen}
          onClose={registrationModalDisclosure.onClose}
          externalRegistrationUrl={event.externalRegistrationUrl}
        />
      ) : (
        <EventRegistrationModal
          isOpen={registrationModalDisclosure.isOpen}
          onClose={registrationModalDisclosure.onClose}
          eventId={event._id}
          eventName={event.name}
          eventStartDate={event.startDate}
          eventEndDate={event.endDate}
          isConference={event.__typename === 'ConferenceEventV2'}
        />
      )}
    </>
  );
};

TicketedEventRegistrationButton.eventFragment = gql`
  fragment TicketedEventRegistrationButton_EventFragment on EventV2 {
    _id
    name
    isExternalRegistrationUrlActive
    externalRegistrationUrl
    ticketsCurrentUserCanPurchase {
      _id
    }
    startDate
    endDate
  }
`;
